import {
  H5,
  Input,
  Stack,
  Inline,
  Checkbox,
  P,
  Box,
  Button,
  Grid
} from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import { useFormContext } from "react-hook-form";
import { useCustomTemplateContext } from "../util/CampaignTemplateContext";
import Divider from "components/Divider";
import { numberValidator } from "utils/react-hook-form";
import { useDisabledFields } from "../../useDisabledFields";
import { useEffect } from "react";
import styled from "styled-components";
import { SelectableChannel } from "./SelectableChannel";
import { getDefaultNotification } from "./defaultNotification";
import { NotificationList } from "./NotificationList";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";
import { useTranslation } from "react-i18next";
import { formMaxWidth } from "../../bundle";
import { ImageTemplates } from "./ImageTemplateList";

interface Props {}

const SUPPORTED_CUSTOM_TEMPLATE_CHANNELS: ProgramService.ChannelType[] = [
  "Unknown",
  "Facebook",
  "Instagram",
  "TikTok",
  "LinkedIn",
  "Google",
  "YouTube"
];

const StyledInline = styled(Inline)`
  width: fit-content;
  border: 1px solid black;
  border-radius: 4px;
`;

export const CustomTemplate = ({}: Props) => {
  const { t } = useTranslation();

  const { register, setValue } = useFormContext<ProgramService.ProgramModel>();
  const {
    campaignKey,
    campaignTemplate: { emails, tickets, invoicedByExternalParty, channel }
  } = useCustomTemplateContext();
  const disabledFields = useDisabledFields();
  const { hq } = useOfficeAndHq();

  useEffect(() => {
    if (invoicedByExternalParty) {
      setValue(`${campaignKey}.percentageOfTotalBudget`, null);
      setValue(
        `${campaignKey}.estimatedExternalPartyPrice.currency`,
        (hq?.currency as ProgramService.Currencies | undefined) ?? "SEK"
      );
    } else {
      // This type is for some reason not generated as nullable but only optional/undefined
      setValue(`${campaignKey}.estimatedExternalPartyPrice`, undefined);
    }
  }, [campaignKey, hq?.currency, invoicedByExternalParty, setValue]);

  return (
    <>
      <Stack spacing="l">
        <H5>{t("programBuilder.program.template.custom.details.header")}</H5>
        <Grid
          columns={["1fr", "1fr 1fr 1fr"]}
          gap="l"
          style={{ maxWidth: formMaxWidth }}
        >
          <Input
            type="text"
            label={t(
              "programBuilder.program.template.custom.details.campaign.label"
            )}
            data-testid={`${campaignKey}-campaign-name`}
            {...register(`${campaignKey}.name`, {
              required: t(
                "programBuilder.program.template.custom.details.campaign.required"
              )
            })}
            readOnly={disabledFields(
              "bundleGroups.bundles.campaignTemplates.name"
            )}
          />
          <Input
            label={t(
              "programBuilder.program.template.custom.details.duration.label"
            )}
            data-testid={`${campaignKey}-duration-days`}
            {...register(`${campaignKey}.durationDays`, {
              valueAsNumber: true,
              required: t(
                "programBuilder.program.template.custom.details.duration.required"
              ),
              validate: numberValidator("Duration"),
              min: {
                value: 0,
                message: t(
                  "programBuilder.program.template.custom.details.duration.validation",
                  {
                    minValue: 0
                  }
                )
              }
            })}
            readOnly={disabledFields(
              "bundleGroups.bundles.campaignTemplates.durationDays"
            )}
          />
          <Input
            label={t(
              "programBuilder.program.template.custom.details.budget.label"
            )}
            data-testid={`${campaignKey}-budget-allocation`}
            {...register(`${campaignKey}.percentageOfTotalBudget`, {
              valueAsNumber: true,
              required: t(
                "programBuilder.program.template.custom.details.budget.required"
              ),
              validate: numberValidator("Budget"),
              disabled: invoicedByExternalParty
            })}
          />
        </Grid>

        <Grid
          columns={["1fr", "1fr 1fr"]}
          gap="l"
          style={{ maxWidth: formMaxWidth }}
        >
          <div style={{ gridColumn: "span 2" }}>
            <Checkbox
              label={t(
                "programBuilder.program.template.custom.details.separateInvoice.checkbox.label"
              )}
              {...register(`${campaignKey}.invoicedByExternalParty`)}
            />
          </div>

          {invoicedByExternalParty && (
            <Input
              label={t(
                "programBuilder.program.template.custom.details.separateInvoice.amount.label"
              )}
              {...register(
                `${campaignKey}.estimatedExternalPartyPrice.amount`,
                {
                  valueAsNumber: true,
                  required: t(
                    "programBuilder.program.template.custom.details.separateInvoice.amount.required"
                  ),
                  validate: numberValidator("Estimated amount"),
                  min: {
                    value: 0,
                    message: t(
                      "programBuilder.program.template.custom.details.separateInvoice.amount.validation"
                    )
                  }
                }
              )}
            />
          )}
        </Grid>

        <Box>
          <H5>
            {t("programBuilder.program.template.custom.details.icon.header")}
          </H5>
          <P>
            {t(
              "programBuilder.program.template.custom.details.icon.description"
            )}
          </P>
        </Box>

        <StyledInline alignItems="center" p="xs">
          {SUPPORTED_CUSTOM_TEMPLATE_CHANNELS.map(availableChannel => (
            <SelectableChannel
              key={availableChannel}
              channel={availableChannel}
              selectedChannel={channel}
            />
          ))}
        </StyledInline>
        <Divider margin="20px 0 16px 0" />

        <ImageTemplates hqId={hq?.id ?? ""} />

        <Divider margin="20px 0 16px 0" />

        <NotificationList
          baseKey={campaignKey}
          emails={emails}
          tickets={tickets}
        />

        <Button
          logLabel={`template-add-notification`}
          variant="text"
          style={{ color: "black" }}
          onClick={() => {
            const ticketsOrDefault: ProgramService.ProgramTicketModel[] =
              tickets ?? [];
            setValue(`${campaignKey}.tickets`, [
              ...ticketsOrDefault,
              getDefaultNotification()
            ]);
          }}
        >
          {t(
            "programBuilder.program.template.custom.details.button.addNotification"
          )}
        </Button>
      </Stack>
    </>
  );
};
